import { type LineBookerSession, type Locale, type PublicEventType, pickLocale } from '@bb/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLineSessionContext } from '@/features/line-app/session-context';
import { publicProfileQuery } from '@/features/public-booking/api';
import { BookingFlow } from '@/features/public-booking/booking-flow';
import { Notice } from './line.app.$connectionId';

/**
 * Booking inside LINE.
 *
 * The customer taps 予約する in the shop's chat and lands here, already signed
 * in: LIFF hands the page an ID token, so there is no login screen and no email
 * to type before they can see a time.
 *
 * The booking itself is the ordinary public flow — same components, same
 * endpoints, same availability rules. Only the identity and the chrome differ.
 * That is on purpose: a second booking implementation for LINE would drift from
 * the first, and the two would disagree about something that matters, like
 * whether a slot is free.
 */
export const Route = createFileRoute('/line/app/$connectionId/')({
  component: LineBookingPage,
});

/** The session is established once by the layout above. */
function LineBookingPage() {
  const { connectionId } = Route.useParams();
  return <LineBooking connectionId={connectionId} session={useLineSessionContext()} />;
}

/**
 * Friend-add, before anything else when the shop requires it.
 *
 * `required` exists because a push only reaches a friend, so without it the
 * shop's confirmations quietly become emails. It is also a step between a
 * customer and a booking, which is why it is the shop's choice and why the
 * screen says what they get for it rather than just demanding a tap.
 */
function FriendGate({ session }: { session: LineBookerSession }) {
  const { t } = useTranslation(['booking']);
  if (!session.addFriendUrl) return null;
  return (
    <div className="mb-4 rounded-lg border border-bb-line bg-bb-surface p-4 text-center">
      <p className="text-sm">{t('booking:line.addFriendBody', { shop: session.shopName })}</p>
      <a
        href={session.addFriendUrl}
        className="mt-3 inline-block min-h-11 rounded bg-bb-accent px-4 py-2.5 text-sm font-medium text-bb-on-accent"
      >
        {t('booking:line.addFriend')}
      </a>
    </div>
  );
}

function LineBooking({
  connectionId,
  session,
}: {
  connectionId: string;
  session: LineBookerSession;
}) {
  const { t, i18n } = useTranslation(['booking', 'common']);
  const locale = i18n.language as Locale;
  const [slug, setSlug] = useState<string | null>(null);
  const profile = useQuery({
    ...publicProfileQuery(session.username ?? ''),
    enabled: Boolean(session.username),
  });

  const blocked = session.friendMode === 'required' && !session.isFriend;

  // No public handle: the shop genuinely has not finished, and only they can
  // fix it. The one case the old shared message described correctly.
  if (!session.username) return <Notice title={t('booking:line.notSetUp')} />;
  if (profile.isLoading) return <Notice title={t('common:status.loading')} />;
  if (profile.isError || !profile.data) {
    return (
      <Notice title={t('booking:line.couldNotLoad')} body={t('booking:line.couldNotLoadBody')} />
    );
  }

  const { host, eventTypes } = profile.data;
  const chosen = eventTypes.find((e: PublicEventType) => e.slug === slug);

  return (
    <main className="mx-auto max-w-lg px-4 py-5">
      {blocked ? <FriendGate session={session} /> : null}
      {session.friendMode === 'suggest' && !session.isFriend && session.addFriendUrl ? (
        <a
          href={session.addFriendUrl}
          className="mb-4 block rounded-lg border border-bb-line bg-bb-surface px-3 py-2 text-center text-xs"
        >
          {t('booking:line.addFriendSuggest', { shop: session.shopName })}
        </a>
      ) : null}

      <div className={blocked ? 'pointer-events-none opacity-40' : ''}>
        {chosen ? (
          <>
            <button
              type="button"
              onClick={() => setSlug(null)}
              className="mb-3 text-sm text-bb-muted"
            >
              ← {t('common:actions.back')}
            </button>
            <BookingFlow
              username={session.username}
              slug={chosen.slug}
              host={host}
              eventType={chosen}
              layout="line"
              lineToken={session.token}
              prefill={{ name: session.displayName, email: session.email }}
            />
          </>
        ) : (
          <>
            <h1 className="mb-1 text-lg font-semibold">{session.shopName}</h1>
            <p className="mb-4 text-sm text-bb-muted">{t('booking:line.pickService')}</p>
            <ul className="space-y-2">
              {eventTypes.map((e: PublicEventType) => (
                <li key={e.slug}>
                  <button
                    type="button"
                    onClick={() => setSlug(e.slug)}
                    className="flex w-full items-center justify-between rounded-lg border border-bb-line bg-bb-surface px-4 py-3 text-left"
                  >
                    <span>
                      <span className="block text-sm font-medium">
                        {pickLocale(e.name, locale)}
                      </span>
                      <span className="block text-xs text-bb-muted">
                        {t('booking:eventTypes.units.minutesShort', { count: e.durationMinutes })}
                      </span>
                    </span>
                    <span aria-hidden>›</span>
                  </button>
                </li>
              ))}
            </ul>
            <Link
              to="/line/app/$connectionId/bookings"
              params={{ connectionId }}
              className="mt-5 block text-center text-sm underline"
            >
              {t('booking:line.myBookings')}
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
